
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { EventBus } from '@/main';
import { PurchaseOrders, PurchaseOrdersFilter } from '@/collections/purchase_orders';
import { StoreLocationList } from '@/collections/store_locations';
import debounce from 'lodash/debounce';
import { CurrencyHelper } from '@/helpers/CurrencyHelper';
import { contextSyncService } from '@/services/contextSync';

/**
 * Chat response structure for PO list display
 */
interface POListChatResponse {
  messages?: Array<{ content: string }>;
  metadata?: Record<string, unknown>;
}

/**
 * Action parameters for PO list filtering
 */
interface POListActionParameters {
  supplier_id?: string;
  supplier_name?: string;
  location_id?: string;
  location_name?: string;
  status?: string;
  from_date?: string;
  to_date?: string;
}

/**
 * Purchase order item for list display
 */
interface PurchaseOrderListItem {
  id: string | number;
  poNumber: string;
  supplierName: string;
  locationName: string;
  status: string;
  orderDate: string;
  expectedDeliveryDate?: string | null;
  totalAmount: number;
}

/**
 * Table pagination and sorting options
 */
interface POListTableOptions {
  page: number;
  itemsPerPage: number;
  sortBy: string[];
  sortDesc: boolean[];
}

@Component({
  name: 'PurchaseOrderListDisplay'
})
export default class PurchaseOrderListDisplay extends Vue {
  @Prop({ default: null }) readonly chatResponse!: POListChatResponse | null;
  @Prop({ default: (): POListActionParameters => ({}) }) readonly actionParameters!: POListActionParameters;

  private purchaseOrdersCollection: PurchaseOrders = new PurchaseOrders();
  private purchaseOrders: PurchaseOrderListItem[] = [];
  private loading = false;
  private searchQuery = '';
  private totalRecords = 0;
  private fetchRequestId = 0;

  // Location lookup map (locationId -> locationName)
  private locationMap: Map<string, string> = new Map();
  private locationsLoaded = false;

  // Expose CurrencyHelper to template
  private CurrencyHelper = CurrencyHelper;
  private tableOptions: POListTableOptions = {
    page: 1,
    itemsPerPage: 10,
    sortBy: ['orderDate'],
    sortDesc: [true]
  };

  private headers = [
    { text: 'PO Number', value: 'poNumber', width: '130px' },
    { text: 'Supplier', value: 'supplierName', width: '120px' },
    { text: 'Location', value: 'locationName', width: '120px' },
    { text: 'Status', value: 'status', width: '100px' },
    { text: 'Date', value: 'orderDate', width: '90px' },
    { text: 'Expected', value: 'expectedDeliveryDate', width: '90px' },
    { text: 'Total', value: 'totalAmount', align: 'right', width: '90px' },
    { text: '', value: 'actions', sortable: false, width: '70px', align: 'center' }
  ];

  /** Debounced search function, initialized in created() */
  private debounceSearch: (() => void) | null = null;

  /** Computed title based on active filters */
  get listTitle(): string {
    const parts: string[] = [];
    const status = this.actionParameters?.status;
    const supplierName = this.actionParameters?.supplier_name;
    const supplierId = this.actionParameters?.supplier_id;
    const fromDate = this.actionParameters?.from_date;
    const toDate = this.actionParameters?.to_date;

    // Status prefix (e.g., "Draft", "Confirmed")
    if (status) {
      parts.push(this.formatStatusForTitle(status));
    }

    // Base title
    parts.push('Purchase Orders');

    // Supplier suffix
    if (supplierName) {
      parts.push(`for ${supplierName}`);
    } else if (supplierId) {
      parts.push('(filtered)');
    }

    // Date range suffix
    if (fromDate || toDate) {
      const dateRange = this.formatDateRange(fromDate, toDate);
      if (dateRange) {
        parts.push(`(${dateRange})`);
      }
    }

    return parts.join(' ') || 'All Purchase Orders';
  }

  /** Check if any filters are active */
  get hasActiveFilters(): boolean {
    return !!(
      this.actionParameters?.supplier_name ||
      this.actionParameters?.supplier_id ||
      this.actionParameters?.location_name ||
      this.actionParameters?.location_id ||
      this.actionParameters?.status ||
      this.actionParameters?.from_date ||
      this.actionParameters?.to_date
    );
  }

  /** Format status for display in title */
  private formatStatusForTitle(status: string): string {
    const statusMap: Record<string, string> = {
      'draft': 'Draft',
      'sent': 'Sent',
      'confirmed': 'Confirmed',
      'partial_received': 'Partially Received',
      'received': 'Received',
      'canceled': 'Canceled',
    };
    return statusMap[status] || status.charAt(0).toUpperCase() + status.slice(1);
  }

  /** Format date range for display */
  private formatDateRange(fromDate?: string, toDate?: string): string {
    if (fromDate && toDate) {
      return `${this.formatShortDate(fromDate)} - ${this.formatShortDate(toDate)}`;
    } else if (fromDate) {
      return `from ${this.formatShortDate(fromDate)}`;
    } else if (toDate) {
      return `until ${this.formatShortDate(toDate)}`;
    }
    return '';
  }

  /** Format date as short string (e.g., "Dec 2025") */
  private formatShortDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }

  created() {
    this.debounceSearch = debounce(this.fetchPurchaseOrders, 500);
  }

  async mounted() {
    // Fetch locations for name lookup, then fetch POs
    await this.fetchLocations();
    await this.fetchPurchaseOrders();
  }

  /** Fetch all locations and build a lookup map */
  private async fetchLocations(): Promise<void> {
    if (this.locationsLoaded) return;

    try {
      const locationList = new StoreLocationList();
      await locationList.fetch();

      // Build lookup map: locationId -> locationName
      for (const location of locationList.items) {
        this.locationMap.set(String(location.id), location.name);
      }
      this.locationsLoaded = true;
    } catch (error) {
      console.error('[PurchaseOrderListDisplay] Failed to load locations:', error);
    }
  }

  /** Get location name by ID */
  private getLocationName(locationId: string | number | null | undefined): string {
    if (!locationId) return '-';
    const name = this.locationMap.get(String(locationId));
    return name || '-';
  }

  @Watch('actionParameters', { deep: true })
  onActionParametersChanged() {
    this.fetchPurchaseOrders();
  }

  private async fetchPurchaseOrders() {
    const { page, itemsPerPage } = this.tableOptions;
    const supplierId = this.actionParameters?.supplier_id;
    const status = this.actionParameters?.status;
    const locationId = this.actionParameters?.location_id;
    const fromDate = this.actionParameters?.from_date;
    const toDate = this.actionParameters?.to_date;

    // Only reserve request ID when actually making a network call
    this.fetchRequestId++;
    const currentRequestId = this.fetchRequestId;
    this.loading = true;

    try {
      const params: PurchaseOrdersFilter = {
        page,
        limit: itemsPerPage,
        // Only include filters if provided
        ...(supplierId && { supplierId: Number(supplierId) }),
        ...(locationId && { locationId: Number(locationId) }),
        ...(status && { status }),
        ...(fromDate && { fromDate }),
        ...(toDate && { toDate }),
        ...(this.searchQuery && { search: this.searchQuery }),
      };

      // Use PurchaseOrders collection to fetch data
      // This handles the correct API base URL and authentication
      await this.purchaseOrdersCollection.fetch(params);

      // Ignore stale responses from older requests
      if (currentRequestId !== this.fetchRequestId) {
        return;
      }

      // Filter to only include items with required fields
      this.purchaseOrders = this.purchaseOrdersCollection.items
        .filter((po): po is typeof po & { id: number } => po.id !== undefined)
        .map(po => ({
          id: po.id,
          poNumber: po.poNumber ?? '',
          supplierName: (po as any).supplierName ?? (po as any).supplier?.name ?? '-',
          locationName: po.locationName ?? this.getLocationName(po.locationId),
          status: po.status ?? 'draft',
          orderDate: po.orderDate ?? '',
          expectedDeliveryDate: po.expectedDeliveryDate ?? null,
          totalAmount: po.totalAmount ?? 0,
        }));
      this.totalRecords = this.purchaseOrdersCollection.total;

      // Sync screen context for automatic context attachment
      this.syncScreenContext();
    } catch (error) {
      console.error('[PurchaseOrderListDisplay] Error fetching POs:', error);

      // Ignore errors from stale requests
      if (currentRequestId !== this.fetchRequestId) {
        return;
      }

      EventBus.$emit('show-snackbar', {
        message: 'Failed to load purchase orders',
        color: 'error'
      });
    } finally {
      // Only update loading state if this is still the current request
      if (currentRequestId === this.fetchRequestId) {
        this.loading = false;
      }
    }
  }

  private getStatusColor(status: string): string {
    switch (status) {
      case 'draft': return 'grey';
      case 'confirmed': return 'info';
      case 'partial_received': return 'orange';
      case 'received': return 'success';
      case 'completed': return 'teal';
      case 'canceled': return 'red';
      default: return 'grey';
    }
  }

  private formatStatus(status: string): string {
    return status ? status.replace(/_/g, ' ').toUpperCase() : '';
  }

  private formatDate(dateString: string): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  }

  private viewDetails(item: PurchaseOrderListItem) {
    // Emit event to display PO directly via API (no chatbox message needed)
    // PurchaseOrderDisplay will fetch the PO by identifier using getByIdentifier API
    EventBus.$emit('po-builder:preview', {
      actions: [{
        action: 'DISPLAY_PURCHASE_ORDER',
        parameters: {
          po_id: String(item.id),
          po_number: item.poNumber
        }
      }],
      current_agent: 'Purchase Order Assistant'
    });
  }

  /**
   * Sync screen context to contextSyncService for automatic context attachment.
   * Called after data loads so chatbox can include screen info in messages.
   */
  private syncScreenContext(): void {
    if (!contextSyncService.getSessionId()) {
      return;
    }

    const supplierId = this.actionParameters?.supplier_id;
    const status = this.actionParameters?.status;

    contextSyncService.updateScreenContext({
      screen_name: 'purchase_order_list',
      screen_path: '/purchase-assistant',
      screen_params: {
        supplier_id: supplierId,
        status: status,
        search: this.searchQuery || undefined,
      },
      component_state: {
        screen_type: 'purchase_order_list',
        screen_data: {
          supplier_id: supplierId,
          status_filter: status,
          search_query: this.searchQuery || undefined,
          total_records: this.totalRecords,
          displayed_count: this.purchaseOrders.length,
          page: this.tableOptions.page,
          items_per_page: this.tableOptions.itemsPerPage,
          // Include summary of displayed POs (limit to avoid large payloads)
          purchase_orders: this.purchaseOrders.slice(0, 20).map(po => ({
            id: po.id,
            po_number: po.poNumber,
            status: po.status,
            order_date: po.orderDate,
            expected_delivery_date: po.expectedDeliveryDate,
            total_amount: po.totalAmount,
          })),
        },
      },
    });
  }
}
